import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { cn } from '../../utils/cn';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from './Button';
import { AccountFormField } from './AccountFormField';

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (password?: string) => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'danger';
  requirePassword?: boolean;
  isSubmitting?: boolean;
  className?: string;
  extraContent?: React.ReactNode;
  confirmDisabled?: boolean;
}

/**
 * Confirmation modal for destructive or important actions
 * Supports password verification for sensitive operations
 */
export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  requirePassword = false,
  isSubmitting = false,
  className,
  extraContent,
  confirmDisabled = false
}) => {
  const [password, setPassword] = useState('');

  // Reset password when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setPassword('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    onConfirm(requirePassword ? password : undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
    if (e.key === 'Enter' && !isSubmitting && (!requirePassword || password)) {
      handleConfirm();
    }
  };

  if (!isOpen) return null;

  const variantClasses = {
    default: {
      icon: 'text-blue-600 dark:text-blue-400',
      button: 'bg-blue-600 hover:bg-blue-700 text-white'
    },
    danger: {
      icon: 'text-red-600 dark:text-red-400',
      button: 'bg-red-600 hover:bg-red-700 text-white'
    }
  };

  // Get or create portal container
  const getPortalContainer = () => {
    let container = document.getElementById('confirmation-modal-root');
    if (!container) {
      container = document.createElement('div');
      container.id = 'confirmation-modal-root';
      document.body.appendChild(container);
    }
    return container;
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div 
        className={cn(
          'bg-background border rounded-lg shadow-lg max-w-md w-full p-4 sm:p-6 space-y-4 sm:space-y-6',
          'max-h-[90vh] overflow-y-auto',
          className
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={cn(
              'p-2 rounded-full flex-shrink-0',
              variant === 'danger' 
                ? 'bg-red-100 dark:bg-red-900/20' 
                : 'bg-blue-100 dark:bg-blue-900/20'
            )}>
              <AlertTriangle className={cn('w-4 h-4 sm:w-5 sm:h-5', variantClasses[variant].icon)} />
            </div>
            <h2 
              id="modal-title"
              className="text-base sm:text-lg font-semibold text-foreground leading-tight"
            >
              {title}
            </h2>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground mobile-touch-target flex-shrink-0"
            disabled={isSubmitting}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="space-y-4 sm:space-y-6">
          <p 
            id="modal-description"
            className="text-sm sm:text-base text-muted-foreground leading-relaxed"
          >
            {description}
          </p>

          {/* Password Field */}
          {requirePassword && (
            <AccountFormField
              id="confirmation-password"
              label="Enter your password to confirm"
              type="password"
              value={password}
              onChange={setPassword}
              placeholder="Your current password"
              required
              disabled={isSubmitting}
              autoComplete="current-password"
            />
          )}

          {extraContent}
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto mobile-touch-target"
          >
            {cancelText}
          </Button>
          
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting || (requirePassword && !password) || confirmDisabled}
            className={cn(variantClasses[variant].button, "w-full sm:w-auto mobile-touch-target")}
          >
            {isSubmitting ? 'Processing...' : confirmText}
          </Button>
        </div>
      </div>
    </div>,
    getPortalContainer()
  );
};
